/**
 * FORGE's guide model (`decisions/0204`): what is left on a build, where each item lands on the
 * PILOT or MECH sheet, when the FORGE key shows, and how it leaves once the build is complete.
 * Pure and React-free, proven in `forgeGuide.test.ts`; `ForgeKey.tsx` renders it and
 * `forgeJump.ts` does the DOM half of a jump.
 *
 * FORGE edits nothing (`decisions/0025`, `0018`): every item only navigates to the sheet in EDIT
 * and focuses the control that fixes it.
 */
import { resolveActiveMechId, type PilotData } from "@lancer-vtt/domain";
import { buildSignals, type BuildAdvisorInput, type BuildSection, type BuildSeverity, type BuildSignal, type LoadoutPart } from "../../lib/editor/buildAdvisor";
import { encodeRailPath, type RailApp, type RailPage } from "./railState";

// ---- anchors ---------------------------------------------------------------------------------

/** A section of the PILOT or MECH sheet in EDIT that a FORGE item lands on (`data-build-anchor`). */
export type BuildAnchor =
  | "callsign"
  | "hase"
  | "triggers"
  | "talents"
  | "licenses"
  | "core-bonuses"
  | "pilot-gear"
  | "mech-frame"
  | "mech-weapons"
  | "mech-systems";

/** The DOM attribute an anchored section carries. */
export const ANCHOR_ATTR = "data-build-anchor";

/** The pilot-scoped sections' anchors. Loadout is mech-scoped and splits by {@link LoadoutPart}. */
export const SECTION_ANCHOR: Record<Exclude<BuildSection, "Loadout">, BuildAnchor> = {
  Licenses: "licenses",
  "Mech Skills": "hase",
  "Skill Triggers": "triggers",
  Talents: "talents",
  "Core Bonuses": "core-bonuses",
  "Pilot Gear": "pilot-gear",
};

export const LOADOUT_ANCHOR: Record<LoadoutPart, BuildAnchor> = {
  frame: "mech-frame",
  weapons: "mech-weapons",
  systems: "mech-systems",
};

/** Which page each anchor lives on. */
export const ANCHOR_PAGE: Record<BuildAnchor, "pilot" | "mech"> = {
  callsign: "pilot",
  hase: "pilot",
  triggers: "pilot",
  talents: "pilot",
  licenses: "pilot",
  "core-bonuses": "pilot",
  "pilot-gear": "pilot",
  "mech-frame": "mech",
  "mech-weapons": "mech",
  "mech-systems": "mech",
};

export function signalAnchor(signal: BuildSignal): BuildAnchor {
  return signal.section === "Loadout" ? LOADOUT_ANCHOR[signal.part ?? "weapons"] : SECTION_ANCHOR[signal.section];
}

// ---- items -----------------------------------------------------------------------------------

/** One line of FORGE's list: the unset callsign, or one build signal. */
export type ForgeItem = {
  /** Stable while the item stands: its anchor and its place among that anchor's items. */
  key: string;
  anchor: BuildAnchor;
  section: BuildSection | "Callsign";
  severity: BuildSeverity;
  message: string;
};

/** Everything left on the build, callsign first, then `buildSignals` in its own order. */
export function forgeItems(input: BuildAdvisorInput): ForgeItem[] {
  const out: ForgeItem[] = [];
  const seen = new Map<BuildAnchor, number>();
  const add = (anchor: BuildAnchor, item: Omit<ForgeItem, "key" | "anchor">) => {
    const n = seen.get(anchor) ?? 0;
    seen.set(anchor, n + 1);
    out.push({ key: `${anchor}#${n}`, anchor, ...item });
  };
  if (input.pilot.callsign.trim().length === 0) add("callsign", { section: "Callsign", severity: "todo", message: "callsign unset" });
  for (const s of buildSignals(input)) add(signalAnchor(s), { section: s.section, severity: s.severity, message: s.message });
  return out;
}

// ---- the guide's lifecycle -------------------------------------------------------------------

/**
 * - `work`: items are left; the key shows, its panel open or closed.
 * - `signoff`: the last item went while the panel was open; VIRGIL signs off until CLOSE FILE.
 * - `flip`: the last item went while the panel was closed; the key reads ✓ COMBAT-LEGAL briefly.
 * - `idle`: complete, nothing shows. New work (a level-up) goes back to `work`.
 */
export type ForgePhase = "idle" | "work" | "signoff" | "flip";

export type ForgeGuideState = { phase: ForgePhase; open: boolean };

export type ForgeGuideEvent =
  | { type: "count"; count: number }
  /** The key: opens or closes the panel; on the ✓ flip it dismisses. */
  | { type: "toggle" }
  | { type: "close-file" }
  | { type: "flip-done" };

/** How long the ✓ COMBAT-LEGAL flip stays before the key leaves. */
export const FLIP_MS = 3500;

/** The guide on first sight of a pilot. `autoOpen` is a brand-new pilot's landing. */
export function forgeGuideStart(count: number, autoOpen: boolean): ForgeGuideState {
  return count > 0 ? { phase: "work", open: autoOpen } : { phase: "idle", open: false };
}

export function forgeGuideStep(state: ForgeGuideState, event: ForgeGuideEvent): ForgeGuideState {
  switch (event.type) {
    case "count":
      if (event.count > 0) return state.phase === "work" || state.phase === "signoff" ? { phase: "work", open: state.open } : { phase: "work", open: false };
      if (state.phase !== "work") return state;
      return state.open ? { phase: "signoff", open: true } : { phase: "flip", open: false };
    case "toggle":
      return state.phase === "work" ? { phase: "work", open: !state.open } : { phase: "idle", open: false };
    case "close-file":
      return { phase: "idle", open: false };
    case "flip-done":
      return state.phase === "flip" ? { phase: "idle", open: false } : state;
  }
}

/** The pages the FORGE key sits on (`decisions/0204` point 1, B1). */
export const FORGE_PAGES: readonly RailApp[] = ["pilot", "mech"];

/**
 * Where the guide shows: the key on PILOT and MECH while it has anything to say, the count as a
 * badge on the Pilot strip icon elsewhere while items are left, and nothing once the build is done.
 */
export function forgeSurface(args: { app: RailApp | undefined; phase: ForgePhase }): "key" | "badge" | "none" {
  const { app, phase } = args;
  if (phase === "idle") return "none";
  if (app !== undefined && FORGE_PAGES.includes(app)) return "key";
  return phase === "work" ? "badge" : "none";
}

/**
 * The pilot the guide follows: the one the PILOT or MECH page shows (the GM's included,
 * `decisions/0204` point 5), else the viewer's selected pilot. The GM has none off those pages.
 */
export function guidedPilotId(args: { page: RailPage | null; viewerPilotId: string | null; pilots: Record<string, PilotData> }): string | undefined {
  const { page, viewerPilotId, pilots } = args;
  if (page?.app === "pilot" && page.pilotId !== undefined) return page.pilotId;
  if (page?.app === "mech" && page.mechId !== undefined && viewerPilotId === null) {
    return Object.values(pilots).find((p) => p.mechs.some((m) => m.id === page.mechId))?.id;
  }
  return viewerPilotId ?? undefined;
}

// ---- SHOW and NEXT ---------------------------------------------------------------------------

/**
 * The page an anchor is on, in EDIT: PILOT for the pilot's sections, MECH on the active mech for
 * Loadout. `viewerPilotId` is a player's selected pilot, whose PILOT page needs no id in the path;
 * the GM's always names the pilot. Already on PILOT, the page keeps its own path and turns EDIT on.
 */
export function jumpPage(args: { anchor: BuildAnchor; pilot: PilotData; current: RailPage | null; viewerPilotId: string | null }): RailPage {
  const { anchor, pilot, current, viewerPilotId } = args;
  if (ANCHOR_PAGE[anchor] === "pilot") {
    if (current?.app === "pilot") return { ...current, mode: "edit" };
    return pilot.id === viewerPilotId ? { app: "pilot", mode: "edit" } : { app: "pilot", pilotId: pilot.id, mode: "edit" };
  }
  const mechId = resolveActiveMechId(pilot);
  return mechId === undefined ? { app: "mech", mode: "hangar" } : { app: "mech", mechId, mode: "edit" };
}

/** Whether a jump has to navigate, or the page already shows. */
export function jumpNavigates(target: RailPage, current: RailPage | null): boolean {
  return current === null || encodeRailPath(target) !== encodeRailPath(current);
}

/** The item NEXT ▸ lands on last. */
export type NextCursor = { key: string; index: number } | null;

/**
 * NEXT ▸ walks the items in order and wraps. When the item it last landed on is gone (fixed), the
 * item that took its place is next, so fixing as you go never skips one. `-1` when nothing is left.
 */
export function nextIndex(items: readonly { key: string }[], last: NextCursor): number {
  if (items.length === 0) return -1;
  if (last === null) return 0;
  const at = items.findIndex((i) => i.key === last.key);
  return at >= 0 ? (at + 1) % items.length : last.index % items.length;
}

// ---- the new-pilot landing -------------------------------------------------------------------

/**
 * Pilots whose guide opens on first sight: "+ New pilot" mints one and lands on PILOT · EDIT with
 * the panel open (`decisions/0204` point 3). Module-level because the request is made before the
 * shell mounts; consuming it clears it, so only the landing opens the panel.
 */
const openOnArrival = new Set<string>();

export function requestForgeOpen(pilotId: string): void {
  openOnArrival.add(pilotId);
}

/** Read during render (twice under StrictMode), so it only peeks; {@link clearForgeOpen} consumes. */
export function forgeOpenRequested(pilotId: string): boolean {
  return openOnArrival.has(pilotId);
}

export function clearForgeOpen(pilotId: string): void {
  openOnArrival.delete(pilotId);
}

/** The new-pilot landing page: the minted pilot's PILOT page in EDIT. */
export const NEW_PILOT_PAGE: RailPage = { app: "pilot", mode: "edit" };
